import React, {useEffect, useMemo, useState} from 'react';
import {
	Button,
	Input,
	Select,
	SelectOption,
	Slider,
	Toast,
	DateRangePicker,
} from '@kaleyra/emmy';
import { useTranslation } from 'react-i18next';
import Styles from './index.module.scss';
import { isEmpty } from 'lodash';
import cn from 'classnames';
import AgentNumber from '../../containers/agentNumber';
import CustomerNumber from '../../containers/customerNumber';
import moment from 'moment';
import { convertArrayToString } from '../../../../utils';
import {TITLE_VALIDATION_REGEX} from "../../../../constants";
import ErrorImage from "../../../../components/ErrorImage/ErrorImage";

export type MapStatePropsType = {
	params: any;
};

export type DispatchPropsType = {
	onClose: () => void;
	onAddAgent: (data: any, callBack: any) => void;
	onEdit: (data: any, callBack: any) => void;
	applyFilters: (filters: any) => void;
	clearFilters: () => void;
};

const StickyAgentModal: React.FC<MapStatePropsType & DispatchPropsType> = ({
	onClose,
	params,
	onAddAgent,
	onEdit,
	applyFilters,
	clearFilters,
}) => {
	const { t } = useTranslation();
	const [title, setTitle] = useState<string|undefined>(undefined);
	// const [inputError, setInputError] = useState(false);
	const [customerNumber, setCustomerNumber] = useState<any>('');
	const [agentNumber, setAgentNumber] = useState<any>('');
	const [id, setID] = useState<any>('');
	const [custChange, setCustChange] = useState<any>({});
	const [agentChange, setAgentChange] = useState<any>({});
	const [statusVal, setStatusVal] = useState<any>();
	const [createdAt, setCreatedAt] = useState<any>();
	const [inProgress, setInProgress] = useState(false);
	const {
		mode,
		agentTag,
		setAgentTag,
		custTag,
		setCustTag,
		dateTag,
		setDateTag,
		statusTag,
		setStatusTag,
		setSearchText,
		searchText,
		enableMasking
	} = params;

	const status = [
		{
			key: '1',
			value: t('mainActive'),
		},
		{
			key: '0',
			value: t('mainInActive'),
		},
	];

	useEffect(() => {
		if (!isEmpty(params?.data)) {
			let receivedData = params?.data;
			setTitle(receivedData?.title);
			setCustomerNumber(receivedData?.customer_number);
			setAgentNumber(receivedData?.agent_number);
			setID(receivedData?.id);
			setStatusVal(receivedData?.status);
		}
	}, [params]);

	useEffect(() => {
		if (mode === 'filter') {
			if (agentTag) {
				setAgentNumber(agentTag);
			}
			if (custTag) {
				setCustomerNumber(custTag);
			}
			if (statusTag) {
				setStatusVal(statusTag);
			}
			if (dateTag) {
				setCreatedAt(dateTag);
			}
		}
	}, []);

	const getFooter = (): React.ReactNode => {
		return (
			<div className={Styles.footerContainer}>
				<Button type="tertiary" onClick={onClose}>
					{t('mainCancel')}
				</Button>
				<Button
					type="primary"
					onClick={() => sendData()}
					disabled={checkButtonDisabled()}
				>
					{mode === 'filter' ? t('apply') : t('mainSave')}
				</Button>
			</div>
		);
	};

	const sendData = () => {
		let data = {
			title: title || "",
			customer_number: customerNumber,
			agent_number: agentNumber,
		};
		if (mode === 'add') {
			setInProgress(true);
			onAddAgent(data, (resp: any) => {
				let obj = {
					title: title || "",
				};
				setInProgress(false);
				if (isEmpty(resp?.error)) {
					if (
						agentTag ||
						statusTag ||
						custTag ||
						dateTag ||
						searchText
					) {
						setDateTag(null);
						setStatusTag('');
						setSearchText('');
						setAgentTag('');
						setCustTag('');
						clearFilters();
					}
					Toast(t('KLR0025', obj), 'success');
					onClose();
				} else {
					Toast(resp.message || t('mainWentWrong'), 'error');
				}
			});
		} else if (mode === 'edit') {
			let pair = { id: id, status: statusVal };
			data = { ...data, ...pair };
			setInProgress(true);
			onEdit(data, (resp: any) => {
				let obj = {
					title: title || "",
				};
				setInProgress(false);
				if (isEmpty(resp?.error)) {
					if (
						agentTag ||
						statusTag ||
						custTag ||
						dateTag ||
						searchText
					) {
						setDateTag(null);
						setStatusTag('');
						setSearchText('');
						setAgentTag('');
						setCustTag('');
						clearFilters();
					}

					Toast(t('KLR0026', obj), 'success');
					onClose();
				} else {
					Toast(resp.message || t('mainWentWrong'), 'error');
				}
			});
		} else if (mode === 'filter') {
			let filters: any = {};
			if (customerNumber) {
				filters.customer_number = customerNumber;
			}
			if (agentNumber) {
				filters.agent_number = agentNumber;
			}
			if (statusVal === '1' || statusVal === '0') {
				filters.status = statusVal.toString();
			}

			if (createdAt) {
				let created_start = moment
					.utc(createdAt[0])
					.format('YYYY-MM-DD');
				let created_end = moment.utc(createdAt[1]).format('YYYY-MM-DD');
				filters.modified_at = created_start + ',' + created_end;
			}

			applyFilters(filters);

			// set the tags
			setCustTag(customerNumber);
			setDateTag(createdAt);
			setAgentTag(agentNumber);
			setStatusTag(statusVal);
			onClose();
		}
	};

	const checkButtonDisabled = () => {
		if(mode !=='filter' && !title) return true;
		let regex = TITLE_VALIDATION_REGEX.test(title || "");
		if ((mode === 'add' || mode === 'edit') && (regex || (title||"").length < 3))
			return true;

		if (
			(mode === 'edit' || mode === 'filter') &&
			agentChange.hasOwnProperty('agentNumValid') &&
			!agentChange['agentNumValid']
		) {
			return true;
		}
		if (
			(mode === 'edit' || mode === 'filter') &&
			custChange.hasOwnProperty('customerNumValid') &&
			!custChange['customerNumValid']
		) {
			return true;
		}
		if (
			mode === 'add' &&
			(!custChange['customerNumValid'] || !agentChange['agentNumValid'])
		) {
			return true;
		}
		if (
			mode === 'filter' &&
			statusVal === undefined &&
			createdAt === undefined &&
			!agentChange.hasOwnProperty('agentNumValid') &&
			!custChange.hasOwnProperty('customerNumValid')
		) {
			return true;
		}
		if ((mode === 'add' || mode === 'edit') && !title) return true;
		if (inProgress) return true;
		return false;
	};

	const getTitle = (): string => {
		if (mode === 'add') {
			return t('KLR0018');
		} else if (mode === 'edit') {
			return t('KLR0024');
		} else if (mode === 'filter') {
			return t('mainFilter');
		}
		return '';
	};

	const validateCheck = useMemo( ():'error'|'default' => {
		if(title === undefined) return 'default';
		let regex = TITLE_VALIDATION_REGEX.test(title);
		if (title.trim().length < 3 || regex) return 'error';
		return 'default';
	},[title]);

	const validateError = useMemo(() => {
		if(title === undefined) return <ErrorImage
			hideIcon={true}
			type="warning"
			title={t('titleSpcCharNotAllowed')}/>;
		if (!title || title.trim().length === 0) {
			return <ErrorImage title={t('titleReq')}/>;
		} else if (TITLE_VALIDATION_REGEX.test(title)) {
			return <ErrorImage title={t('titleSpcCharNotAllowed')}/>
		} else if (title.trim().length < 3) {
			return <ErrorImage title={t('min3Char')}/>;
		}
		return <ErrorImage
			hideIcon={true}
			type="warning"
			title={t('titleSpcCharNotAllowed')} />;
	},[title]);

	const renderBody = () => {
		return (
			<div className={Styles.container}>
				<Input
					value={title}
					onChange={(e) => {
						let val = e.target.value;
						// if (val.length > 75) return;
						setTitle((val||"").slice(0,75));
					}}
					label={t('mainTitle')}
					placeholder={t('mainTitle')}
					className={cn(Styles.ipElement)}
					style={{backgroundColor: validateCheck === "error" ? "rgba(241, 108, 123, 0.1)" : ""}}
					bottomLabelType={ validateCheck }
					bottomLabel={ validateError }
					showBottomLabelSpace
				/>
				<CustomerNumber
					value={mode === 'add' ? '' : params?.data?.customer_number}
					onChange={(val) => setCustomerNumber(val)}
					setDisable={(val) => setCustChange(val)}
					oneNumber={mode === 'add' ? false : true}
					enableMasking={enableMasking}
				/>

				{mode === 'add' ? (
					<div className={Styles.helpTxt}>{t('KLR0023')}</div>
				) : undefined}

				<AgentNumber
					value={mode === 'add' ? '' : params?.data?.agent_number}
					onChange={(val) => setAgentNumber(val)}
					setDisable={(val) => setAgentChange(val)}
				/>
				{mode === 'edit' ? (
					<Select
						label={t('statusPlaceholder')}
						placeholder={t('statusPlaceholder')}
						onChange={(e: any) => {
							setStatusVal(e as string);
						}}
						value={statusVal}
						className={Styles.ipElement}
						defaultValue={
							params?.statusTag === '0' ||
							params?.statusTag === '1'
								? params?.statusTag
								: undefined
						}
					>
						{status.map((item) => (
							<SelectOption value={item?.key} key={item?.key}>
								{item?.value}
							</SelectOption>
						))}
					</Select>
				) : undefined}
			</div>
		);
	};

	const disabledDate = (current: any) => {
		// Can not select days after today
		return current && current > moment().endOf('day');
	};

	const renderFilterBody = () => {
		return (
			<div className={Styles.container}>
				<div className={Styles.ipElement}>{t('mainLastUpdatedOn')}</div>
				<DateRangePicker
					cancelBtnText={t('mainCancel')}
					placeholder={[t('flwMailFrom'), t('mainTo')]}
					okBtnText={t('done')}
					inputStyle={Styles.datePickerWrapper}
					value={params?.dateTag}
					onOk={(e: any) => {
						setCreatedAt(e);
					}}
					disabledDate={disabledDate}
					showBottomLabelSpace={false}
				/>
				<CustomerNumber
					value={params?.custTag}
					onChange={(val) => {
						setCustomerNumber(convertArrayToString(val));
					}}
					setDisable={(val) => setCustChange(val)}
					oneNumber={false}
					modeFilter={true}
					enableMasking={enableMasking}
				/>
				<AgentNumber
					modeFilter
					value={params?.agentTag}
					onChange={(val) => {
						setAgentNumber(convertArrayToString(val));
					}}
					setDisable={(val) => setAgentChange(val)}
				/>
				<Select
					label={t('statusPlaceholder')}
					placeholder={t('statusPlaceholder')}
					onChange={(e: any) => {
						setStatusVal(e as string);
					}}
					value={statusVal}
					className={Styles.ipElement}
					defaultValue={
						params?.statusTag === '0' || params?.statusTag === '1'
							? params?.statusTag
							: undefined
					}
				>
					{status.map((item) => (
						<SelectOption value={item?.key} key={item?.key}>
							{item?.value}
						</SelectOption>
					))}
				</Select>
			</div>
		);
	};

	const getContent = () => {
		if (mode === 'add' || mode === 'edit') {
			return renderBody();
		} else if (mode === 'filter') {
			return renderFilterBody();
		}
		return '';
	};

	return (
		<Slider
			containerId="root-modal"
			onClose={onClose}
			visibility={true}
			footer={getFooter()}
			title={getTitle()}
		>
			{getContent()}
		</Slider>
	);
};

export default StickyAgentModal;
